import { ofetch, FetchError } from 'ofetch';
import { ApiClientError } from './errors';
import { createDebugLogger } from '../utils/debug';
import { attachBearer } from '../auth/attachBearer';

/**
 * #116 — Fabrik fuer die produktspezifischen BFF-Proxy-Clients.
 *
 * Bisher hatte jedes API-Produkt (ebill/events/price/probe/wp/...) eine fast
 * identische Client-Datei: `ofetch.create({ baseURL: '/proxy/<name>' })`, ein
 * Fetch-Wrapper der FetchError -> ApiClientError uebersetzt, und ein
 * `is<Name>ProxyEnabled()` das `window.__ORBIT_CONFIG__.<name>ProxyEnabled`
 * liest. Das war ~9x Copy-Paste. Diese Fabrik kapselt genau dieses Muster;
 * ein Produkt-Client wird damit zum Dreizeiler und bleibt in Signatur/Verhalten
 * identisch (schrittweise Migration, nichts bricht).
 *
 * @param {string} name  Produktname = Proxy-Segment (z. B. 'ebill').
 * @param {object} [opts]
 * @param {string} [opts.configFlag]  Schluessel in window.__ORBIT_CONFIG__
 *   (Default `${name}ProxyEnabled`).
 * @returns {{ fetch: Function, isEnabled: Function, proxyUrl: Function, baseURL: string }}
 */
export function createProxyClient(name, { configFlag = `${name}ProxyEnabled` } = {}) {
  const dlog = createDebugLogger(name);
  const baseURL = `/proxy/${name}`;

  const raw = ofetch.create({
    baseURL,
    onRequest(ctx) { attachBearer(ctx); dlog('→', ctx.request); }, // #501
    onResponse({ request, response }) { dlog('←', request, response.status); },
  });

  async function apiFetch(path, opts = {}) {
    try {
      return await raw(path, opts);
    } catch (err) {
      if (err instanceof FetchError) {
        const data = err.data;
        throw new ApiClientError({
          code: data?.error?.code ?? `${name}_request_failed`,
          message: data?.error?.message ?? err.message,
          status: err.response?.status ?? 0,
          details: data?.error?.details,
        });
      }
      throw err;
    }
  }

  function isEnabled() {
    if (typeof window === 'undefined') return false;
    return window.__ORBIT_CONFIG__?.[configFlag] === true;
  }

  /** Absoluter Proxy-Pfad (z. B. fuer Downloads/<a href>), ohne Doppel-Slash. */
  function proxyUrl(path) {
    return `${baseURL}/${String(path ?? '').replace(/^\/+/, '')}`;
  }

  return { fetch: apiFetch, isEnabled, proxyUrl, baseURL };
}
